import { default as http } from "../rest/http";
import { fetchOrganizationDetails, fetchOrganizationHierarchy } from "./OrganizationService";

// The v3 organization endpoints are camelCase and paginated. They are not
// interchangeable with the v1 /master-data/organizations endpoints — do not
// mix their types or responses.
const BASE_PATH = "/master-data/v3/organizations";

// Guards against cyclic parent links in bad data; real hierarchies are shallow.
const MAX_BREADCRUMB_DEPTH = 50;

// Minimal org shape needed to render one breadcrumb segment.
export interface BreadcrumbOrg {
  id: number;
  name: string;
  parentOrgId: number | null;
}

// v1 org payloads (/organizations/{id} and /organizations/{id}/parent-hierarchy)
// return `id` as a number but `parentOrgId` as a string ("2"). Coerce both so id
// lookups and parent walks compare correctly.
const toBreadcrumbOrg = (org: {
  id: number | string;
  name: string;
  parentOrgId?: number | string | null;
}): BreadcrumbOrg => ({
  id: Number(org.id),
  name: org.name,
  parentOrgId: org.parentOrgId == null || org.parentOrgId === "" ? null : Number(org.parentOrgId),
});

// The parent-hierarchy payload is a bare array today; tolerate an `items` wrapper.
const normalizeHierarchy = (hierarchy: unknown): unknown[] => {
  if (Array.isArray(hierarchy)) return hierarchy;
  const items = (hierarchy as { items?: unknown[] } | null)?.items;
  return Array.isArray(items) ? items : [];
};

// Resolves the ancestors of `orgId`, root-first and excluding the org itself, for
// the breadcrumb. Seeds from the v1 parent-hierarchy endpoint (one call), then
// walks parentOrgId links and fetches any ancestor that endpoint left out — it
// returns the chain relative to the caller's own organization, so e.g. "Finley"
// is missing for a Finley admin. Fetching on a miss keeps the trail complete
// regardless of who is logged in.
export const fetchOrganizationAncestors = async (orgId: number): Promise<BreadcrumbOrg[]> => {
  const known = new Map<number, BreadcrumbOrg>();
  normalizeHierarchy(await fetchOrganizationHierarchy(orgId)).forEach((raw) => {
    const org = toBreadcrumbOrg(raw as Parameters<typeof toBreadcrumbOrg>[0]);
    known.set(org.id, org);
  });

  const resolveOrg = async (id: number): Promise<BreadcrumbOrg> =>
    known.get(id) ?? toBreadcrumbOrg(await fetchOrganizationDetails(id));

  const current = await resolveOrg(orgId);
  const chain: BreadcrumbOrg[] = [];
  const visited = new Set<number>([current.id]);
  let parentId = current.parentOrgId;
  while (parentId != null && !visited.has(parentId) && chain.length < MAX_BREADCRUMB_DEPTH) {
    visited.add(parentId);
    const parent = await resolveOrg(parentId);
    chain.unshift(parent);
    parentId = parent.parentOrgId;
  }
  return chain;
};

export interface OrganizationsV3QueryParams {
  parentId?: number; // omit for top-level organizations
  search?: string;
  page: number;
  perPage: number;
}

// GET /master-data/v3/organizations — one page of orgs at a single hierarchy level.
export const fetchOrganizationsV3 = async (params: OrganizationsV3QueryParams) => {
  const query = new URLSearchParams();
  if (params.parentId != null) query.append("parentId", String(params.parentId));
  if (params.search) query.append("search", params.search);
  query.append("page", String(params.page));
  query.append("perPage", String(params.perPage));

  const response = await http.get(`${BASE_PATH}?${query.toString()}`);
  return response;
};

// GET /master-data/v3/organizations/stats — the two summary cards. Without
// parentId the totals are system-wide; with parentId they are the recursive
// totals under that organization.
export const fetchOrganizationsV3Stats = async (parentId?: number) => {
  const url =
    parentId != null ? `${BASE_PATH}/stats?parentId=${parentId}` : `${BASE_PATH}/stats`;
  const response = await http.get(url);
  return response;
};

// GET /master-data/v3/organizations/{orgId}/deactivation-preview — cascade counts
// for the deactivate modal. Super Admin / Finley Admin only (Coach gets 403).
export const fetchDeactivationPreview = async (orgId: number) => {
  const response = await http.get(`${BASE_PATH}/${orgId}/deactivation-preview`);
  return response;
};

// cascadeSubOrgs also sets the scope of the other three options: the org alone
// when false, the whole subtree when true (see ADM-11-deactivate-organization-api.md).
export interface DeactivateOrganizationBody {
  cascadeSubOrgs: boolean;
  cascadeUsers: boolean;
  revokeSessions: boolean;
  notifyOrgAdmins: boolean;
}

// POST /master-data/v3/organizations/{orgId}/deactivate — synchronous and one-way;
// there is no reactivation endpoint. Everything runs in one transaction; a
// 400/403/404 means nothing was written.
export const deactivateOrganization = async (
  orgId: number,
  body: DeactivateOrganizationBody
) => {
  const response = await http.post(`${BASE_PATH}/${orgId}/deactivate`, body);
  return response;
};

export interface RenameOrganizationBody {
  name: string;
}

// PATCH /master-data/v3/organizations/{orgId} — rename an organization (ADM-12).
// `name` is trimmed server-side and must be 1–87 characters. Super Admin /
// Finley Admin only (Coach gets 403). Responds with the updated list row plus
// previousName/renamed (see ADM-12-rename-organization-api.md).
export const renameOrganization = async (orgId: number, body: RenameOrganizationBody) => {
  const response = await http.patch(`${BASE_PATH}/${orgId}`, body);
  return response;
};
